import { Button } from '@teach/ui'
import { cn } from '@/lib/utils'
import { failureText } from '@/features/ai/lib/failureText'
import { useTopUp } from '@/features/ai/credits/CreditsDialogContext'
import type { StartFailure } from '@/features/ai/lib/parseStartError'
import type { StudioJobType } from '@/features/ai/studioTypes'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  failure: StartFailure
  /**
   * Повторный вызов того же старта с теми же параметрами. Кнопка рисуется
   * ТОЛЬКО когда обработчик передан — решает РОДИТЕЛЬ, а не баннер: он один
   * знает, достижимо ли действие в текущем состоянии (I-5). Например,
   * `AISidebar` не передаёт его при `isInputDisabled`, `HomeLanding` — на
   * пустом/переросшем промпте.
   */
  onRetry?: () => void
  /**
   * Открыть чужой прогон. Только когда `failure.kind === 'course_busy'` и
   * обработчик передан — на главной курса ещё нет, показывать прогон
   * некуда, поэтому `HomeLanding` его не передаёт вовсе.
   */
  onShowRun?: (jobId: number, jobType: StudioJobType) => void
  /**
   * Увести автора в поле стиля картинок курса. Как и у соседних действий,
   * рисуется только когда обработчик передан: знает о достижимости экрана
   * родитель, а не баннер.
   */
  onOpenStyle?: () => void
  /**
   * Признак `useReserveReturning`. Считает его РОДИТЕЛЬ: баннер не знает
   * курса, а на главной курса нет вовсе (там он всегда ложный).
   */
  reserveReturning?: boolean
  /** Разметка (отступы/выравнивание) — забота вызывающего экрана, не баннера. */
  className?: string
  /**
   * Класс параграфа с текстом отказа. По умолчанию `text-xs` (сайдбар).
   * `className` лежит на обёртке и текста не достаёт — прямое правило на
   * элементе наследование не перебивает (CLAUDE.md, анти-паттерн, ловили
   * четырежды), поэтому это ОТДЕЛЬНЫЙ проп, а не расчёт на каскад. Главная
   * передаёт `text-sm text-center` — просторная страница, длинный текст с
   * суммами кредитов должен переноситься по центру, а не влево.
   */
  textClassName?: string
}

/**
 * Единственный баннер отказа старта — используется и в сайдбаре редактора, и
 * на главной (M-4: раньше на главной жил самостоятельный `FailureNotice`,
 * рисовавший тот же текст другим размером). Копирайт целиком из
 * `failureText`; действие рисуется ТОЛЬКО когда оно есть в `FailureView.action`
 * И обработчик для него передан.
 */
export const FailureBanner = ({
  failure,
  onRetry,
  onShowRun,
  onOpenStyle,
  reserveReturning,
  className,
  textClassName,
}: Props) => {
  const view = failureText(failure, { reserveReturning })
  const { open: openTopUp } = useTopUp()
  const m = useMessages(messages)
  return (
    <div role="status" className={cn('space-y-1', className)}>
      <p className={cn('text-xs text-destructive', textClassName)}>{view.title}</p>
      {/* Действие на этом баннере — единственный выход из «прогон не
          стартовал», поэтому оно такое же, как у соседних плашек панели
          (`CourseBusyNotice`/`JobLostNotice`) и как в рефе
          (`EditorAISidebar.tsx:920`): полноширинная outline-кнопка, а не
          текстовая ссылка 12px. Кегль не зашит — его даёт `size="sm"`, иначе
          на главной сообщение 14px спорило с действием 12px под ним. */}
      {view.action === 'retry' && onRetry ? (
        <Button variant="outline" size="sm" className="w-full" onClick={onRetry}>
          {m.retry}
        </Button>
      ) : null}
      {view.action === 'show-run' && failure.kind === 'course_busy' && onShowRun ? (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onShowRun(failure.jobId, failure.jobType)}
        >
          {/* M-4 (финальное ревью): та же смета в `CourseBusyNotice` на
              `awaiting_author` зовёт «Ответить на вопрос», а не «Показать
              запуск» — докблок выше прямо обещает паритет. */}
          {failure.status === 'awaiting_author' ? m.answerQuestion : m.showRun}
        </Button>
      ) : null}
      {view.action === 'style' && onOpenStyle ? (
        <Button variant="outline" size="sm" className="w-full" onClick={onOpenStyle}>
          {m.setImageStyle}
        </Button>
      ) : null}
      {/* Единственное действие баннера, которое не приходит пропом: диалог
          пополнения глобальный и достижим с любого экрана, в отличие от
          «повторить»/«показать запуск»/«стиль», где достижимость знает только
          родитель. Дефицит собирается ТОЛЬКО из полной пары чисел — по
          контракту `required`/`balance` опциональны, а пустая строка сломала бы
          денежное сравнение внутри диалога. */}
      {view.action === 'topup' ? (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() =>
            openTopUp(
              failure.kind === 'no_credits' && failure.required && failure.balance
                ? { balance: failure.balance, required: failure.required }
                : null,
              // Витрина ЯВНО, а не по наличию дефицита: числа в отказе
              // опциональны, и без них автор, у которого прогон не запустился
              // из-за денег, попадал бы в окно чтения истории вместо покупки.
              'packages',
            )
          }
        >
          {m.topUp}
        </Button>
      ) : null}
    </div>
  )
}
